import React, { useState, useEffect, useMemo } from 'react';
import { Search, Filter, X, ChevronDown } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import './SearchFilters.css';

const SearchFilters = ({ topics, onFiltered }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedDifficulty, setSelectedDifficulty] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [subjects, setSubjects] = useState([]);

  // Extract unique subjects from topics
  useEffect(() => {
    const uniqueSubjects = [...new Set(topics.map(t => t.subject).filter(Boolean))];
    setSubjects(uniqueSubjects.sort());
  }, [topics]);

  // Filter topics based on search and filters
  const filteredTopics = useMemo(() => {
    return topics.filter(topic => {
      const matchesSearch = !searchTerm || 
        topic.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        topic.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (topic.seoTags && topic.seoTags.some(tag => 
          tag.toLowerCase().includes(searchTerm.toLowerCase())
        ));

      const matchesSubject = !selectedSubject || topic.subject === selectedSubject;
      
      const matchesDifficulty = !selectedDifficulty || topic.difficulty === selectedDifficulty;

      return matchesSearch && matchesSubject && matchesDifficulty;
    });
  }, [topics, searchTerm, selectedSubject, selectedDifficulty]);

  // Notify parent component of filtered results
  useEffect(() => {
    onFiltered(filteredTopics);
  }, [filteredTopics]);

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedSubject('');
    setSelectedDifficulty('');
  };

  const activeFilterCount = [searchTerm, selectedSubject, selectedDifficulty].filter(Boolean).length;

  return (
    <div className="search-filters-wrapper">
      {/* Search Bar */}
      <div className="search-bar-container">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-sapphire/60 dark:text-gold/60" size={20} />
          <Input
            type="text"
            placeholder="Search courses, topics, skills..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-12 py-3 search-input w-full"
          />
        </div>

        {/* Filter Toggle Button */}
        <Button
          onClick={() => setShowFilters(!showFilters)}
          variant="outline"
          className="relative filter-toggle-btn"
        >
          <Filter size={18} />
          <span>Filters</span>
          {activeFilterCount > 0 && (
            <Badge className="absolute -top-2 -right-2 bg-gold text-velvet text-xs">
              {activeFilterCount}
            </Badge>
          )}
        </Button>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="filter-panel animate-slideDown">
          <div className="filter-header">
            <h3 className="font-semibold text-sapphire dark:text-cream-light">Filter Results</h3>
            {activeFilterCount > 0 && (
              <Button
                onClick={clearFilters}
                variant="ghost"
                size="sm"
                className="text-gold hover:text-gold/80"
              >
                <X size={16} className="mr-1" />
                Clear All
              </Button>
            )}
          </div>

          <div className="filters-grid">
            {/* Subject Filter */}
            <div className="filter-group">
              <label className="filter-label">
                <ChevronDown size={16} />
                Subject
              </label>
              <select
                value={selectedSubject}
                onChange={(e) => setSelectedSubject(e.target.value)}
                className="filter-select"
              >
                <option value="">All Subjects</option>
                {subjects.map(subject => (
                  <option key={subject} value={subject}>
                    {subject}
                  </option>
                ))}
              </select>
            </div>

            {/* Difficulty Filter */}
            <div className="filter-group">
              <label className="filter-label">
                <ChevronDown size={16} />
                Difficulty
              </label>
              <select
                value={selectedDifficulty}
                onChange={(e) => setSelectedDifficulty(e.target.value)}
                className="filter-select"
              >
                <option value="">All Levels</option>
                <option value="Beginner">Beginner</option>
                <option value="Intermediate">Intermediate</option>
                <option value="Advanced">Advanced</option>
                <option value="Expert">Expert</option>
              </select>
            </div>
          </div>

          {/* Active Filters Display */}
          {activeFilterCount > 0 && (
            <div className="active-filters">
              {searchTerm && (
                <Badge variant="secondary" className="filter-badge">
                  Search: {searchTerm}
                  <X
                    size={14}
                    className="ml-2 cursor-pointer"
                    onClick={() => setSearchTerm('')}
                  />
                </Badge>
              )}
              {selectedSubject && (
                <Badge variant="secondary" className="filter-badge">
                  Subject: {selectedSubject}
                  <X
                    size={14}
                    className="ml-2 cursor-pointer"
                    onClick={() => setSelectedSubject('')}
                  />
                </Badge>
              )}
              {selectedDifficulty && (
                <Badge variant="secondary" className="filter-badge">
                  Level: {selectedDifficulty}
                  <X
                    size={14}
                    className="ml-2 cursor-pointer"
                    onClick={() => setSelectedDifficulty('')}
                  />
                </Badge>
              )}
            </div>
          )}

          {/* Results Count */}
          <div className="results-count">
            Found <span className="font-bold text-gold">{filteredTopics.length}</span> course{filteredTopics.length !== 1 ? 's' : ''}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchFilters;
